/**
 * #2263 — the words for a re-solve held for review on dates members already
 * have, and the way out: "Discard the held re-solve". Since #2571 every
 * re-solve of published dates is held this way, not only the ones the
 * self-check held.
 *
 * While a held re-solve waits, a cover on a slot it changes is refused (the
 * roster that is out stays what members see) with a 409 that starts with
 * `HELD_RESOLVE_REFUSAL_PREFIX` (the API's `HELD_RESOLVE_PENDING_PREFIX`).
 * Wherever that refusal shows, the lead gets the discard button beside it;
 * the roster page's header shows the same button on the held re-solve's line.
 */
import { ApiError } from "../api/client";

/** The API's 409 starts with this sentence (`_pending_seat_guard.py`). */
export const HELD_RESOLVE_REFUSAL_PREFIX = "A re-solve held for your review changes this day.";

/** The button. */
export const DISCARD_HELD_RESOLVE_LABEL = "Discard the held re-solve";

/** Whether an error (an `ApiError`, or the text a surface already shows) is
 *  the held re-solve's refusal. */
export function isHeldResolveRefusal(error: unknown): boolean {
  if (error instanceof ApiError) {
    return error.status === 409 && error.detail.startsWith(HELD_RESOLVE_REFUSAL_PREFIX);
  }
  return typeof error === "string" && error.startsWith(HELD_RESOLVE_REFUSAL_PREFIX);
}

function seats(n: number): string {
  return `${n} published ${n === 1 ? "seat" : "seats"}`;
}

/** The header line: *"A re-solve is held for your review · 2
 *  hard-rule problems · it would change 3 published seats"*. A re-solve that
 *  replaces no published seat says what it adds instead (#2571). */
export function heldResolveLine(held: {
  rule_problems: number;
  seats_replaced: number;
  drafts?: number;
  via_release?: boolean;
}): string {
  const parts = ["A re-solve is held for your review"];
  if (held.rule_problems > 0) {
    parts.push(
      `${held.rule_problems} hard-rule ${held.rule_problems === 1 ? "problem" : "problems"}`,
    );
  }
  if (held.seats_replaced > 0) {
    parts.push(`it would change ${seats(held.seats_replaced)}`);
  } else if ((held.drafts ?? 0) > 0) {
    const n = held.drafts ?? 0;
    parts.push(`it would add ${n} ${n === 1 ? "seat" : "seats"}`);
  }
  // A scoped or by-group re-solve is released from its Release card.
  if (held.via_release) parts.push("release it from the solve card");
  return parts.join(" · ");
}

/** After the discard: what happened, and what to do next. */
export function heldResolveDiscardedLine(
  result: { drafts_removed: number; seats_kept: number },
  then = "Try the cover again.",
): string {
  const kept = result.seats_kept > 0 ? ` and ${seats(result.seats_kept)} kept` : "";
  return `Held re-solve discarded: the roster that is out stands${kept}. ${then}`.trim();
}

/** The 404: nothing was held any more (published, discarded or replaced). */
export const NOTHING_HELD_LINE =
  "No re-solve is held on these dates any more. The roster has been refreshed.";
